import { useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, IconButton, Slide, Stack } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useHomeNavItems } from './useMarketingNavItems';
import MarketingNavDrawerPanel from './MarketingNavDrawerPanel';
import MarketingNavDesktopItem from './MarketingNavDesktopItem';
import { MarketingNavGalleryPanel } from './MarketingNavGalleryMegaPanel';
import { MarketingNavMegaMenuProvider } from './MarketingNavGalleryContext';
import { MarketingNavNewsMediaPanel } from './MarketingNavNewsMediaMegaPanel';
import { MarketingNavStudioPanel } from './MarketingNavStudioMegaPanel';
import { MarketingNavTradePanel } from './MarketingNavTradeMegaPanel';
import useMarketingNavDrawerItems from './useMarketingNavDrawerItems';
import { NiicoLogoHeader } from '@/components/Logos';
import {
  HOME_NAV_BAR_PX,
  HOME_NAV_BAR_PY,
  HOME_NAV_DESKTOP_DISPLAY,
  HOME_NAV_LINK_HOVER_UNDERLINE_COLOR,
  HOME_NAV_MOBILE_DISPLAY,
  HOME_NAV_STACK_SPACING,
} from './home-landing-data';

const LOGO_HERO_STYLES = {
  width: 'auto',
  height: '100%',
  maxHeight: 52,
};

const HERO_MENU_ICON_SX = { color: 'rgba(255,255,255,0.88)' };

export default function HomeHeroNav({ logoHref = '#top' }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navItems = useHomeNavItems();
  const drawerItems = useMarketingNavDrawerItems(navItems);

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <MarketingNavMegaMenuProvider>
      <Slide direction="down" in timeout={500}>
        <Box
          component="nav"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: HOME_NAV_BAR_PX,
              py: HOME_NAV_BAR_PY,
            }}
          >
            <Link href={logoHref} style={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ height: { xs: 40, md: 52 }, display: 'flex', alignItems: 'center' }}>
                <NiicoLogoHeader logoContext="hero" styles={LOGO_HERO_STYLES} />
              </Box>
            </Link>

            <Box
              sx={{
                display: HOME_NAV_DESKTOP_DISPLAY,
                alignItems: 'center',
                gap: HOME_NAV_STACK_SPACING,
              }}
            >
              <Stack
                direction="row"
                spacing={HOME_NAV_STACK_SPACING}
                sx={{ alignItems: 'stretch' }}
              >
                {navItems.map(item => (
                  <MarketingNavDesktopItem
                    key={item.label}
                    item={item}
                    underlineColor={HOME_NAV_LINK_HOVER_UNDERLINE_COLOR}
                    color="rgba(255,255,255,0.88)"
                    activeColor="#fff"
                  />
                ))}
              </Stack>
              <IconButton aria-label="Open menu" onClick={openDrawer} sx={HERO_MENU_ICON_SX}>
                <MenuIcon />
              </IconButton>
            </Box>

            <IconButton
              aria-label="Open menu"
              onClick={openDrawer}
              sx={{
                display: HOME_NAV_MOBILE_DISPLAY,
                ...HERO_MENU_ICON_SX,
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <MarketingNavGalleryPanel />
          <MarketingNavNewsMediaPanel />
          <MarketingNavTradePanel />
          <MarketingNavStudioPanel />
          <MarketingNavDrawerPanel
            open={drawerOpen}
            onOpen={openDrawer}
            onClose={closeDrawer}
            items={drawerItems}
          />
        </Box>
      </Slide>
    </MarketingNavMegaMenuProvider>
  );
}

HomeHeroNav.propTypes = {
  logoHref: PropTypes.string,
};
